import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import {
  setPickupLocation,
  setDropLocation,
  setSelectedVehicle,
  setSenderContact,
  setReceiverContact,
  setParcelDetails,
  setEstimatedCost,
} from '../store/slices/deliverySlice';
import LocationSelector from './LocationSelector';
import ContactSelector from './ContactSelector';
import VehicleSelector from './VehicleSelector';
import SavedAddresses from './SavedAddresses';
import { ROUTES } from '../config/appConfig';
import { vehicleTypes as sharedVehicleTypes } from '../config/companyConfig';
import '../styles/DeliveryForm.css';

const validationSchema = Yup.object().shape({
  parcelSize: Yup.string().required('Please select parcel size'),
  parcelWeight: Yup.number()
    .positive('Weight must be positive')
    .required('Parcel weight is required'),
  parcelDescription: Yup.string().required('Please describe the parcel'),
});

const PARCEL_SIZES = {
  SMALL: { label: 'Small (0-1 kg)', value: 'SMALL', maxWeight: 1 },
  MEDIUM: { label: 'Medium (1-5 kg)', value: 'MEDIUM', maxWeight: 5 },
  LARGE: { label: 'Large (5-10 kg)', value: 'LARGE', maxWeight: 10 },
  XLARGE: { label: 'Extra Large (10+ kg)', value: 'XLARGE', maxWeight: Infinity },
};

const VEHICLE_TYPES = {
  BIKE: { label: '🏍️ Bike', value: 'BIKE', costPerKm: 5, maxWeight: 1 },
  SCOOTER: { label: '🛵 Scooter', value: 'SCOOTER', costPerKm: 7, maxWeight: 5 },
  CAR: { label: '🚗 Car', value: 'CAR', costPerKm: 15, maxWeight: 10 },
  TRUCK: { label: '🚚 Truck', value: 'TRUCK', costPerKm: 25, maxWeight: 50 },
};

const DASHBOARD_VEHICLE_TYPES = {
  BIKE: { ...sharedVehicleTypes.BIKE, ...VEHICLE_TYPES.BIKE, name: sharedVehicleTypes.BIKE.name, icon: sharedVehicleTypes.BIKE.icon, capacity: sharedVehicleTypes.BIKE.capacity },
  SCOOTER: { ...sharedVehicleTypes.SCOOTER, ...VEHICLE_TYPES.SCOOTER, name: sharedVehicleTypes.SCOOTER.name, icon: sharedVehicleTypes.SCOOTER.icon, capacity: sharedVehicleTypes.SCOOTER.capacity },
  CAR: { ...sharedVehicleTypes.CAR, ...VEHICLE_TYPES.CAR, name: sharedVehicleTypes.CAR.name, icon: sharedVehicleTypes.CAR.icon, capacity: sharedVehicleTypes.CAR.capacity },
  TRUCK: { ...sharedVehicleTypes.TRUCK, ...VEHICLE_TYPES.TRUCK, name: sharedVehicleTypes.TRUCK.name, icon: sharedVehicleTypes.TRUCK.icon, capacity: sharedVehicleTypes.TRUCK.capacity },
};

const normalizeAccountPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const getAccountContact = (profile, user) => {
  const source = profile || {};
  const fallback = user || {};
  const name = [
    source.firstName || source.first_name || fallback.firstName || fallback.first_name,
    source.lastName || source.last_name || fallback.lastName || fallback.last_name,
  ].filter(Boolean).join(' ').trim();
  const phone = normalizeAccountPhone(
    source.phone ||
    source.mobile ||
    source.mobileNumber ||
    source.phoneNumber ||
    fallback.phone ||
    fallback.mobile ||
    fallback.mobileNumber ||
    fallback.phoneNumber
  );

  return { name, phone };
};

const DeliveryForm = ({ accountProfile }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const {
    loading,
    error,
    pickupLocation,
    dropLocation,
    selectedVehicle,
    senderContact,
    receiverContact,
    parcelDetails,
    estimatedCost,
  } = useSelector((state) => state.delivery);
  const selectedVehicleDetails = selectedVehicle ? DASHBOARD_VEHICLE_TYPES[selectedVehicle] : null;
  const accountContact = getAccountContact(accountProfile, user);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storedRoute = window.sessionStorage.getItem('landingRouteDraft');
    const storedVehicle = window.sessionStorage.getItem('landingSelectedVehicle');

    if (storedRoute && (!pickupLocation || !dropLocation)) {
      try {
        const parsedRoute = JSON.parse(storedRoute);

        if (parsedRoute.pickupLocation && !pickupLocation) {
          dispatch(setPickupLocation(parsedRoute.pickupLocation));
        }

        if (parsedRoute.dropLocation && !dropLocation) {
          dispatch(setDropLocation(parsedRoute.dropLocation));
        }

        window.sessionStorage.removeItem('landingRouteDraft');
      } catch (error) {
        console.error('Failed to parse landing route draft', error);
      }
    }

    if (storedVehicle && !selectedVehicle) {
      dispatch(setSelectedVehicle(storedVehicle));
      window.sessionStorage.removeItem('landingSelectedVehicle');
    }
  }, [dispatch, dropLocation, pickupLocation, selectedVehicle]);

  useEffect(() => {
    if (!pickupLocation || !dropLocation || !selectedVehicle) {
      if (estimatedCost) {
        dispatch(setEstimatedCost(0));
      }
      return;
    }

    const distance = calculateDistance(pickupLocation, dropLocation);
    dispatch(setEstimatedCost(calculateEstimatedCost(distance, selectedVehicle)));
  }, [dispatch, dropLocation, estimatedCost, pickupLocation, selectedVehicle]);

  const calculateEstimatedCost = (distance, vehicle) => {
    if (vehicle && DASHBOARD_VEHICLE_TYPES[vehicle]) {
      return distance * DASHBOARD_VEHICLE_TYPES[vehicle].costPerKm;
    }
    return 0;
  };

  const handleLocationSelect = (location, type) => {
    if (type === 'pickup') {
      dispatch(setPickupLocation(location));
    } else {
      dispatch(setDropLocation(location));
    }
  };

  const handleVehicleSelect = (vehicleType) => {
    dispatch(setSelectedVehicle(vehicleType));
  };

  const calculateDistance = (from, to) => {
    // Simple distance calculation (in real app, use Google Maps API)
    const R = 6371;
    const lat1 = (from.lat * Math.PI) / 180;
    const lat2 = (to.lat * Math.PI) / 180;
    const deltaLat = ((to.lat - from.lat) * Math.PI) / 180;
    const deltaLng = ((to.lng - from.lng) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handleFormSubmit = (values) => {
    if (!pickupLocation || !dropLocation || !selectedVehicle || !senderContact || !receiverContact) {
      alert('Please fill all required fields');
      return;
    }

    dispatch(
      setParcelDetails({
        parcelSize: values.parcelSize,
        parcelWeight: Number(values.parcelWeight),
        parcelDescription: values.parcelDescription,
      })
    );
    navigate(ROUTES.checkout);
  };

  return (
    <div className="delivery-form-container">
      <div className="flow-banner mb-4">
        <div className="flow-banner-copy">
          <span className="flow-banner-label">Dashboard Booking</span>
          <h4>Add parcel details and contacts, then continue to checkout.</h4>
        </div>
        <div className="flow-steps">
          <span className="flow-step">Route saved</span>
          <span className="flow-step is-current">Parcel and contacts</span>
          <span className="flow-step">Checkout</span>
        </div>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4 shadow-sm booking-section-card">
        <Card.Header className="bg-primary text-white">
          <Card.Title className="mb-0">📍 Location Details</Card.Title>
        </Card.Header>
        <Card.Body>
          <SavedAddresses pickupLocation={pickupLocation} senderContact={senderContact || accountContact} onSelect={(item) => {
            dispatch(setPickupLocation(item.location));
            dispatch(setSenderContact({ name: item.contactName || '', phone: item.contactPhone || '' }));
          }} />
          <Row className="mb-3">
            <Col md={6}>
              <LocationSelector
                label="Pickup Location"
                location={pickupLocation}
                onSelect={(loc) => handleLocationSelect(loc, 'pickup')}
              />
            </Col>
            <Col md={6}>
              <LocationSelector
                label="Drop Location"
                location={dropLocation}
                onSelect={(loc) => handleLocationSelect(loc, 'drop')}
              />
            </Col>
          </Row>

          {pickupLocation && dropLocation && (
            <Row>
              <Col>
                <p className="text-muted">
                  <strong>Distance:</strong> {calculateDistance(pickupLocation, dropLocation).toFixed(2)} km
                </p>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4 shadow-sm booking-section-card">
        <Card.Header className="bg-primary text-white">
          <Card.Title className="mb-0">📦 Parcel Details</Card.Title>
        </Card.Header>
        <Card.Body>
          <Formik
            initialValues={{
              parcelSize: parcelDetails.parcelSize || '',
              parcelWeight: parcelDetails.parcelWeight || '',
              parcelDescription: parcelDetails.parcelDescription || '',
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Parcel Size</Form.Label>
                      <Form.Select
                        name="parcelSize"
                        value={values.parcelSize}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.parcelSize && !!errors.parcelSize}
                      >
                        <option value="">Select Size...</option>
                        {Object.keys(PARCEL_SIZES).map((key) => (
                          <option key={key} value={key}>
                            {PARCEL_SIZES[key].label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.parcelSize}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Weight (kg)</Form.Label>
                      <Form.Control
                        type="number"
                        name="parcelWeight"
                        value={values.parcelWeight}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.parcelWeight && !!errors.parcelWeight}
                        placeholder="Enter weight"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.parcelWeight}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Parcel Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="parcelDescription"
                    value={values.parcelDescription}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.parcelDescription && !!errors.parcelDescription}
                    placeholder="Describe what's in the parcel"
                    rows={3}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.parcelDescription}
                  </Form.Control.Feedback>
                </Form.Group>

                {selectedVehicle ? (
                  <div className="vehicle-preserved-note">
                    Vehicle was selected before login. You can continue with parcel details and contacts below.
                  </div>
                ) : (
                  <VehicleSelector
                    vehicles={DASHBOARD_VEHICLE_TYPES}
                    selectedVehicle={selectedVehicle}
                    onSelect={handleVehicleSelect}
                  />
                )}

                {selectedVehicleDetails && (
                  <div className="selected-vehicle-banner mb-3">
                    <span className="selected-vehicle-label">Selected vehicle</span>
                    <div className="selected-vehicle-row">
                      <strong>{selectedVehicleDetails.label}</strong>
                      <span>{selectedVehicleDetails.maxWeight} kg max</span>
                      <span>Rs {selectedVehicleDetails.costPerKm}/km</span>
                    </div>
                  </div>
                )}

                <Row className="mb-3">
                  <Col md={6}>
                    <ContactSelector
                      label="Sender Contact"
                      contact={senderContact}
                      accountContact={accountContact}
                      onSelect={(contact) => dispatch(setSenderContact(contact))}
                    />
                  </Col>
                  <Col md={6}>
                    <ContactSelector
                      label="Receiver Contact"
                      contact={receiverContact}
                      accountContact={accountContact}
                      onSelect={(contact) => dispatch(setReceiverContact(contact))}
                    />
                  </Col>
                </Row>

                {estimatedCost > 0 && (
                  <Card className="mb-3 bg-light cost-preview-card">
                    <Card.Body>
                      <span className="cost-preview-label">Estimated Delivery Cost</span>
                      <h5>₹{estimatedCost.toFixed(2)}</h5>
                      <p className="mb-0 text-muted">Final amount is confirmed before payment.</p>
                    </Card.Body>
                  </Card>
                )}

                <div className="form-action-row">
                  <Button
                    variant="success"
                    type="submit"
                    className="proceed-btn"
                    disabled={loading || !pickupLocation || !dropLocation || !selectedVehicle}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      'Continue to payment'
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DeliveryForm;
